/**
 * When a common-area booking is for.
 *
 * A whole-day booking is normalised by the backend to the wall clock 00:00:00–23:59:59 of the
 * chosen day (common_area_service.go:217-223, "Whatever time-of-day the client sent is
 * discarded"). Those two times are an artifact of "the whole day", not hours anyone picked — so
 * rendering them as a range is wrong twice over: it invents a 23:59 the resident never chose, and
 * putting them through `new Date().toLocaleTimeString()` re-reads that wall clock as an instant
 * and shifts it into the browser's zone. In UTC-3 that turned a booking for the 31st into
 * "30/10/26 21:00 – 20:59".
 *
 * So whole-day bookings show the date and the SPACE'S OPERATING HOURS — "Sáb, 31 de out de 2026 ·
 * 08:00 às 17:00". Not "Dia inteiro": that phrase tells a resident nothing they did not already
 * know, whereas when the salão actually opens and closes is the thing they need. The date is read
 * from the ISO string's own calendar fields — never through a Date, which is what moved the day.
 *
 * TIME-SLOT bookings are deliberately NOT handled here. Those have real hours a resident chose,
 * and whether the API returns them offset-preserved or UTC-normalised decides whether the browser
 * should convert them. That is unverified, the existing rendering is not reported as wrong, and
 * guessing it would risk breaking the times that currently work. `wholeDayLabel` returns null for
 * them so the caller keeps doing exactly what it already did.
 */

const MONTHS_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/**
 * The booking's own wall clock, as an ISO string in UTC — the basis of both readers below.
 *
 * NOT the browser's timezone, and NOT the digits as they arrive either. `start_time` is a Postgres
 * timestamptz, and GORM renders it in the DATABASE SESSION's timezone, which nothing pins: the
 * integration harness answers `2026-10-20T21:00:00-03:00` for the booking a client sent as
 * `2026-10-21T07:00:00Z`. Same instant, different calendar fields, so reading the digits is only
 * right while the server happens to answer in UTC.
 *
 * UTC is the booking's own clock: the clients send the chosen hours with a `Z`, and the backend
 * normalises a whole-day booking to 00:00–23:59 in the offset it received. Normalising back to UTC
 * is identity for a `Z` answer and recovers the booked day and hour from any other offset.
 */
function utcFields(iso: string | null | undefined): string | null {
  const raw = (iso || '').trim();
  if (!raw) return null;
  // A zoneless stamp is already a wall clock — reading it as local would move it.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(raw)) return raw;
  const at = new Date(raw);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The YYYY-MM-DD the booking is ON. Null when there is no date to read. */
export function bookingCalendarDate(iso: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(utcFields(iso) || '');
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

/** HH:MM of the booking, read on the same clock. */
export function wallClock(iso: string | null | undefined): string | null {
  const match = /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})/.exec(utcFields(iso) || '');
  return match ? `${match[1]}:${match[2]}` : null;
}

/** "Sáb, 31 de out de 2026" — weekday first, month in words so 31/10 can't be read as 10/31. */
function longDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  // Noon UTC: far enough from either midnight that no timezone can shift the weekday.
  const at = new Date(Date.UTC(y, m - 1, d, 12));
  const weekday = at.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'UTC' })
    .replace('.', '')
    .replace(/^./, (c) => c.toUpperCase());
  return `${weekday}, ${String(d).padStart(2, '0')} de ${MONTHS_PT[m - 1]} de ${y}`;
}

/**
 * True when the booking covers a full day by its own times, regardless of what the area says now.
 * An area switched from whole_day to time_slot must not turn its existing bookings back into a
 * bogus 21:00–20:59 range.
 */
function spansWholeDay(startIso: string, endIso: string): boolean {
  const start = wallClock(startIso);
  const end = wallClock(endIso);
  // The app sends 23:59; the backend normalises to 23:59:59. Both are "the whole day".
  return start === '00:00' && end === '23:59';
}

export interface OperatingWindow {
  open: string;
  close: string;
}

interface AreaHours {
  opening_time?: string;
  closing_time?: string;
  operating_hours?: Array<{ weekday: number; open: string; close: string }>;
}

/**
 * Which opening/closing window applies on a given YYYY-MM-DD.
 *
 * Per-weekday hours (migration 226) override the area's global window for that weekday; weekdays
 * with no entry fall back to opening_time/closing_time. The weekday is derived with Date.UTC so
 * no local timezone can shift the date onto the neighbouring day — the same hazard this whole
 * module exists to avoid.
 */
export function operatingHoursForDate(
  area: AreaHours | null | undefined,
  isoDate: string | null | undefined,
): OperatingWindow | null {
  if (!area || !isoDate) return null;

  const [y, m, d] = isoDate.split('-').map(Number);
  if (y && m && d) {
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    const entry = area.operating_hours?.find((h) => h.weekday === weekday);
    if (entry?.open && entry?.close) return { open: entry.open, close: entry.close };
  }

  if (area.opening_time && area.closing_time) {
    return { open: area.opening_time, close: area.closing_time };
  }
  return null;
}

/**
 * The label for a whole-day booking, or null when this is a time-slot booking the caller should
 * render its own way.
 */
export function wholeDayLabel(
  startIso: string,
  endIso: string,
  bookingMode: 'whole_day' | 'time_slot' | undefined,
  hours: OperatingWindow | null | undefined,
): string | null {
  const isWholeDay =
    bookingMode === 'whole_day' || (bookingMode !== 'time_slot' && spansWholeDay(startIso, endIso));
  if (!isWholeDay) return null;

  const date = bookingCalendarDate(startIso);
  if (!date) return null;
  // Date alone when the area's hours are unknown — better than asserting a window we don't have.
  return hours ? `${longDate(date)} · ${hours.open} às ${hours.close}` : longDate(date);
}
